<script setup lang="ts">
   import { computed, onMounted, reactive, ref } from 'vue';
   import { definePageMeta, setPageLayout, useHead, useRoute, useRuntimeConfig } from '#imports';
   import { useTournamentEntryForm } from '~/composables/useTournamentEntryForm';
   import { submitTournamentEntry } from '~/composables/submitTournamentEntry';
   import { formatDateString } from '~/functions/parseDateString';
   import { clubDisplayName, paymentCtaLabel, validPayment } from '~/functions/tournamentEntry';
   import type {
      TournamentEntryFieldValue,
      TournamentEntryFormField,
      TournamentEntryFormTournament,
      TournamentEntryRosterRow,
      TournamentEntrySubmitErrors,
   } from '~/types/types';

   definePageMeta({
      layout: false,
   });

   const config = useRuntimeConfig();
   const route = useRoute();
   const portalBrand = config.public.portalBrand as string;

   // Brand-specific layout selection. As new brands adopt this feature, add their layout here.
   const LAYOUT_BY_BRAND: Record<string, string> = {
      'wow-polo': 'wow-polo',
   };
   const resolvedLayout = LAYOUT_BY_BRAND[portalBrand];
   if (resolvedLayout) {
      setPageLayout(resolvedLayout);
   }

   useHead({
      title: 'Tournament Entry',
   });

   const { form, isLoading, notFound, error: fetchError, fetchForm } = useTournamentEntryForm(portalBrand);

   const tournamentIds = ref<number[]>([]);
   const submitterEmail = ref('');
   const submitterPhone = ref('');
   const teamName = ref('');
   const formData = reactive<Record<string, TournamentEntryFieldValue>>({});

   const validationErrors = ref<TournamentEntrySubmitErrors>({});
   const submitState = ref<'idle' | 'submitting' | 'success' | 'rate_limited' | 'error'>('idle');
   const submittedEntryId = ref<number | null>(null);
   const submittedTournamentIds = ref<number[]>([]);
   const submitErrorMessage = ref('');

   const isInputFieldType = (
      field: TournamentEntryFormField
   ): field is Exclude<TournamentEntryFormField, { type: 'html_content' }> => field.type !== 'html_content';

   const initialiseFormData = () => {
      if (!form.value) return;
      for (const field of form.value.form_schema) {
         if (!isInputFieldType(field)) continue;
         if (field.name in formData) continue;

         if (field.type === 'checkbox') {
            const hasOptions = Array.isArray(field.options) && field.options.length > 0;
            formData[field.name] = hasOptions ? [] : false;
            continue;
         }
         if (field.type === 'multiselect') {
            formData[field.name] = [];
            continue;
         }
         if (field.type === 'team_roster') {
            formData[field.name] = [];
            continue;
         }
         formData[field.name] = '';
      }
   };

   const prefillTournamentFromQuery = () => {
      if (!form.value) return;
      const raw = route.query.tournament;
      const queryId = Array.isArray(raw) ? raw[0] : raw;
      if (!queryId) return;
      const parsed = Number(queryId);
      if (!Number.isFinite(parsed)) return;
      const matches = form.value.tournaments.some((t) => t.tournament_id === parsed);
      if (matches) tournamentIds.value = [parsed];
   };

   onMounted(async () => {
      await fetchForm();
      initialiseFormData();
      prefillTournamentFromQuery();
   });

   const isDisabled = computed(
      () => submitState.value === 'submitting' || submitState.value === 'success'
   );

   const pickInputFields = (): Record<string, TournamentEntryFieldValue> => {
      const out: Record<string, TournamentEntryFieldValue> = {};
      if (!form.value) return out;
      for (const field of form.value.form_schema) {
         if (!isInputFieldType(field)) continue;
         if (field.name in formData) out[field.name] = formData[field.name];
      }
      return out;
   };

   const handleSubmit = async () => {
      if (isDisabled.value || !form.value) return;
      submitState.value = 'submitting';
      validationErrors.value = {};
      submitErrorMessage.value = '';

      const result = await submitTournamentEntry({
         tournament_ids: tournamentIds.value,
         submitter_email: submitterEmail.value,
         ...(submitterPhone.value ? { submitter_phone: submitterPhone.value } : {}),
         team_name: teamName.value.trim(),
         form_data: pickInputFields(),
      });

      if (result.status === 'success') {
         submitState.value = 'success';
         submittedEntryId.value = result.entryId;
         submittedTournamentIds.value = [...tournamentIds.value];
         return;
      }
      if (result.status === 'validation') {
         submitState.value = 'idle';
         validationErrors.value = result.errors;
         return;
      }
      if (result.status === 'rate_limited') {
         submitState.value = 'rate_limited';
         return;
      }
      submitState.value = 'error';
      submitErrorMessage.value = result.message;
   };

   const setFieldValue = (name: string, value: TournamentEntryFieldValue) => {
      formData[name] = value;
   };

   const fieldError = (name: string) => {
      const key = `form_data.${name}` as `form_data.${string}`;
      return validationErrors.value[key]?.[0];
   };

   const rosterFieldErrors = (name: string): Record<string, string> => {
      const out: Record<string, string> = {};
      const prefix = `form_data.${name}.`;
      const errors = validationErrors.value as Record<string, string[] | undefined>;
      for (const key of Object.keys(errors)) {
         if (!key.startsWith(prefix)) continue;
         const sub = key.substring(prefix.length);
         const msg = errors[key]?.[0];
         if (msg) out[sub] = msg;
      }
      return out;
   };

   const tournamentIdsError = computed(() => validationErrors.value.tournament_ids?.[0]);
   const submitterEmailError = computed(() => validationErrors.value.submitter_email?.[0]);
   const submitterPhoneError = computed(() => validationErrors.value.submitter_phone?.[0]);
   const teamNameError = computed(() => validationErrors.value.team_name?.[0]);

   const closeLabel = (iso: string | null) => {
      if (!iso) return null;
      const dateOnly = iso.split('T')[0];
      return formatDateString(dateOnly);
   };

   const dateRangeLabel = (start: string | null, end: string | null) => {
      if (!start && !end) return null;
      if (start && end) return `${formatDateString(start)} – ${formatDateString(end)}`;
      return formatDateString(start ?? end);
   };

   /**
    * The tournaments the user actually ticked, restricted to those with a payment link.
    *
    * The form response already carries every payment block, so the success panel needs no
    * second request - the submit response deliberately returns nothing but the entry id.
    * There is no registration-state gate here: this list can only be non-empty for a form
    * that was just submitted, which is only possible while entries are open.
    */
   const submittedPayments = computed(() => {
      if (!form.value) return [];

      return form.value.tournaments
         .filter((t) => submittedTournamentIds.value.includes(t.tournament_id))
         .map((t) => ({ tournament: t, payment: validPayment(t.payment) }))
         .filter((row) => row.payment !== null);
   });

   const anyPaymentRequired = computed(() =>
      submittedPayments.value.some((row) => row.payment?.required)
   );

   // Club, dates and the closing date share one muted line beneath the tournament name.
   // Four separate chips on a single flex row wrapped badly on narrow screens, and every
   // part is optional, so they are joined only once the empty ones are dropped.
   const tournamentMetaLabel = (t: TournamentEntryFormTournament) => {
      const closes = closeLabel(t.registration_closes_at);

      return [
         clubDisplayName(t.club),
         dateRangeLabel(t.startDate, t.endDate),
         closes ? `Closes ${closes}` : null,
      ]
         .filter(Boolean)
         .join(' · ');
   };
</script>

<template>
   <article class="mx-auto max-w-2xl px-5 pb-16 pt-8">
      <div v-if="isLoading" class="px-4 py-12 text-center text-gray-600">
         <p>Loading…</p>
      </div>

      <div v-else-if="notFound" class="px-4 py-12 text-center text-gray-600">
         <h1 class="mb-3 text-2xl font-bold text-gray-900">Tournament Entry</h1>
         <p>Tournament entries are not currently being collected for this brand.</p>
      </div>

      <div v-else-if="fetchError" class="px-4 py-12 text-center text-red-700">
         <h1 class="mb-3 text-2xl font-bold text-red-700">Something went wrong</h1>
         <p>We couldn't load the tournament entry form. Please refresh the page or try again later.</p>
      </div>

      <template v-else-if="form">
         <header class="form-hero mb-8 border-b border-gray-200 pb-6 text-center" v-html="form.title" />

         <div v-if="form.tournaments.length === 0" class="px-4 py-12 text-center text-gray-600">
            <p>No tournaments are currently accepting entries. Please check back soon.</p>
         </div>

         <form v-else class="flex flex-col" novalidate @submit.prevent="handleSubmit">
            <fieldset class="mb-5 rounded-lg border border-gray-200 p-5">
               <legend class="px-2 text-base font-semibold text-gray-900">
                  Select tournaments
                  <span class="ml-0.5 text-red-700" aria-hidden="true">*</span>
               </legend>
               <p class="mb-3 mt-1 text-sm text-gray-500">
                  Please select the tournaments you plan to enter.*
               </p>

               <label
                  v-for="t in form.tournaments"
                  :key="t.tournament_id"
                  class="flex items-start gap-x-3 border-b border-dashed border-gray-100 py-2.5 last:border-b-0">
                  <input
                     v-model="tournamentIds"
                     type="checkbox"
                     class="mt-1 shrink-0"
                     :value="t.tournament_id"
                     :disabled="isDisabled" />
                  <span class="flex min-w-0 flex-col gap-0.5">
                     <span class="font-semibold text-gray-800">
                        {{ t.name }}
                        <span v-if="t.tournamentHandicap" class="font-normal text-gray-500">
                           ({{ t.tournamentHandicap }})
                        </span>
                     </span>
                     <span v-if="tournamentMetaLabel(t)" class="text-sm text-gray-500">
                        {{ tournamentMetaLabel(t) }}
                     </span>
                  </span>
               </label>

               <p v-if="tournamentIdsError" class="mt-2 text-xs text-red-700">{{ tournamentIdsError }}</p>
            </fieldset>

            <fieldset class="mb-5 rounded-lg border border-gray-200 p-5">
               <legend class="px-2 text-base font-semibold text-gray-900">Contact details</legend>

               <div class="mt-3 flex flex-col gap-1.5">
                  <label for="te-submitter-email" class="text-sm font-semibold text-gray-800">
                     Email
                     <span class="ml-0.5 text-red-700" aria-hidden="true">*</span>
                  </label>
                  <input
                     id="te-submitter-email"
                     v-model="submitterEmail"
                     type="email"
                     required
                     :disabled="isDisabled"
                     class="rounded-md border bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500/20 disabled:bg-gray-100 disabled:text-gray-500"
                     :class="submitterEmailError ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500'" />
                  <p v-if="submitterEmailError" class="text-xs text-red-700">{{ submitterEmailError }}</p>
               </div>

               <div class="mt-3 flex flex-col gap-1.5">
                  <label for="te-submitter-phone" class="text-sm font-semibold text-gray-800">Phone</label>
                  <input
                     id="te-submitter-phone"
                     v-model="submitterPhone"
                     type="tel"
                     :disabled="isDisabled"
                     class="rounded-md border bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500/20 disabled:bg-gray-100 disabled:text-gray-500"
                     :class="submitterPhoneError ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500'" />
                  <p v-if="submitterPhoneError" class="text-xs text-red-700">{{ submitterPhoneError }}</p>
               </div>
            </fieldset>

            <fieldset class="mb-5 rounded-lg border border-gray-200 p-5">
               <legend class="px-2 text-base font-semibold text-gray-900">
                  Team name
                  <span class="ml-0.5 text-red-700" aria-hidden="true">*</span>
               </legend>

               <div class="mt-3 flex flex-col gap-1.5">
                  <label for="te-team-name" class="text-sm font-semibold text-gray-800">
                     Team name
                     <span class="ml-0.5 text-red-700" aria-hidden="true">*</span>
                  </label>
                  <input
                     id="te-team-name"
                     v-model="teamName"
                     type="text"
                     required
                     maxlength="255"
                     :disabled="isDisabled"
                     class="rounded-md border bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-cyan-500/20 disabled:bg-gray-100 disabled:text-gray-500"
                     :class="teamNameError ? 'border-red-700' : 'border-gray-300 focus:border-cyan-500'" />
                  <p class="text-xs text-gray-500">
                     This appears in the email subject and the admin's submissions list.
                  </p>
                  <p v-if="teamNameError" class="text-xs text-red-700">{{ teamNameError }}</p>
               </div>
            </fieldset>

            <template v-for="(field, index) in form.form_schema" :key="`schema-${index}`">
               <TournamentEntryFieldHtmlContent
                  v-if="field.type === 'html_content'"
                  :html="field.html" />

               <TournamentEntryFieldInput
                  v-else-if="['text', 'textarea', 'email', 'number', 'date'].includes(field.type)"
                  :field="field as any"
                  :model-value="formData[field.name] as string | number | undefined"
                  :error="fieldError(field.name)"
                  :disabled="isDisabled"
                  @update:model-value="(value: string | number) => setFieldValue(field.name, value)" />

               <TournamentEntryFieldChoice
                  v-else-if="['select', 'radio', 'multiselect', 'checkbox'].includes(field.type)"
                  :field="field as any"
                  :model-value="formData[field.name] as string | string[] | boolean | undefined"
                  :error="fieldError(field.name)"
                  :disabled="isDisabled"
                  @update:model-value="(value: string | string[] | boolean) => setFieldValue(field.name, value)" />

               <TournamentEntryFieldTeamRoster
                  v-else-if="field.type === 'team_roster'"
                  :field="field as any"
                  :model-value="formData[field.name] as TournamentEntryRosterRow[] | undefined"
                  :errors="rosterFieldErrors(field.name)"
                  :disabled="isDisabled"
                  @update:model-value="(rows: TournamentEntryRosterRow[]) => setFieldValue(field.name, rows)" />

               <TournamentEntryFieldSignature
                  v-else-if="field.type === 'signature'"
                  :field="field as any"
                  :model-value="formData[field.name] as string | undefined"
                  :error="fieldError(field.name)"
                  :disabled="isDisabled"
                  @update:model-value="(value: string) => setFieldValue(field.name, value)" />
            </template>

            <div
               v-if="submitState === 'success'"
               class="my-5 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800"
               role="status">
               <h2 class="mb-1 text-lg font-bold">
                  {{ submittedPayments.length ? 'Step 1 complete — your entry has been received.' : 'Thanks — your entry has been received.' }}
               </h2>
               <p>
                  We have your submission on record (reference #{{ submittedEntryId }}). The tournament organisers will follow up by email shortly.
               </p>

               <!--
                  One pay button per ticked tournament, read from the form response we
                  already hold. The entry fee is charged on the organiser's own Stripe
                  page, so nothing here can be verified by us - the team name is the only
                  value that crosses into their account and makes the payment matchable.
               -->
               <div v-if="submittedPayments.length" class="mt-4 border-t border-green-200 pt-4">
                  <h3 class="text-base font-bold">Step 2 — pay your entry fee</h3>

                  <p v-if="anyPaymentRequired" class="mt-1 text-sm">
                     Your place is not confirmed until payment is received.
                  </p>

                  <p v-if="teamName.trim()" class="mt-1 text-sm">
                     Enter the team name <strong>{{ teamName.trim() }}</strong> on the payment page so the organiser can match your payment to this entry.
                  </p>

                  <div v-for="row in submittedPayments" :key="row.tournament.tournament_id" class="mt-4">
                     <p v-if="submittedPayments.length > 1" class="mb-1 text-sm font-semibold">
                        {{ row.tournament.name }}
                     </p>

                     <a
                        :href="row.payment?.url"
                        :data-testid="`entry-payment-link-${row.tournament.tournament_id}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-block rounded-full bg-[#5cc8d9] px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90">
                        {{ paymentCtaLabel(row.payment) }}
                     </a>

                     <span v-if="row.payment?.amount_label" class="ml-3 text-sm font-semibold">
                        {{ row.payment.amount_label }}
                     </span>

                     <p v-if="row.payment?.note" class="mt-1 text-xs text-green-700">
                        {{ row.payment.note }}
                     </p>
                  </div>
               </div>
            </div>

            <div
               v-else-if="submitState === 'rate_limited'"
               class="my-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
               role="alert">
               Too many submissions from this network. Please try again later.
            </div>

            <div
               v-else-if="submitState === 'error'"
               class="my-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
               role="alert">
               {{ submitErrorMessage || 'Submission failed. Please try again.' }}
            </div>

            <div class="mt-5 flex justify-end">
               <button
                  type="submit"
                  class="rounded-full bg-[#5cc8d9] px-7 py-3 text-base font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="isDisabled">
                  <span v-if="submitState === 'submitting'">Submitting…</span>
                  <span v-else-if="submitState === 'success'">Entry submitted</span>
                  <span v-else>Submit entry</span>
               </button>
            </div>
         </form>
      </template>
   </article>
</template>

<style scoped>
   /* v-html content can't carry Tailwind utility classes, so style the sanitized
      HTML the API returns via :deep() — this is the one place custom CSS is needed. */
   .form-hero :deep(h2) {
      font-size: 1.875rem;
      font-weight: 700;
      line-height: 1.2;
      color: #111827;
      margin-bottom: 0.5rem;
   }

   .form-hero :deep(h3) {
      font-size: 1.25rem;
      color: #4b5563;
      margin-top: 0.5rem;
   }

   .form-hero :deep(p) {
      font-size: 1rem;
      color: #4b5563;
   }

   .form-hero :deep(a) {
      color: #2563eb;
      text-decoration: underline;
   }

   .form-hero :deep(ul) {
      list-style: none;
      padding-left: 0;
      margin-bottom: 0.75rem;
      text-align: left;
      display: inline-block;
   }

   .form-hero :deep(ul li) {
      position: relative;
      padding-left: 1.5rem;
      margin-bottom: 0.5rem;
   }

   .form-hero :deep(ul li::before) {
      content: '';
      position: absolute;
      left: 0.25rem;
      top: 0.6rem;
      width: 0.4rem;
      height: 0.4rem;
      border-radius: 9999px;
      background-color: var(--lms-color-brand-primary);
   }

   .form-hero :deep(ul li p) {
      margin: 0;
   }
</style>
